import Vue from 'vue';
import VueRouter from 'vue-router';
import _ from 'underscore';
import { getSafeReturnUrl } from '@/util/return_url';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    meta: { secure: true },
    redirect: { name: 'training' }
  },
  {
    path: '/contact/update',
    name: 'contact-information-form',
    meta: { secure: true },
    component: () => import(/* webpackChunkName: "training" */ '@/views/details/ContactInformationForm')
  },
  {
    path: '/about',
    name: 'about',
    meta: { secure: true },
    component: () => import(/* webpackChunkName: "about" */ '@/pages/about/About')
  },
  {
    path: '/login',
    name: 'login',
    meta: { clearSession: true },
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/LoginView')
  },
  {
    path: '/account/create',
    name: 'account-creation',
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/AccountCreation')
  },
  {
    path: '/account/activate',
    name: 'account-activation',
    meta: { clearSession: true },
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/AccountActivation')
  },
  {
    path: '/account/username',
    name: 'request-username',
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/RequestUsername')
  },
  {
    path: '/account/lookup',
    name: 'alternate-account-lookup',
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/AlternateAccountLookup')
  },
  {
    path: '/account/reset-password',
    name: 'request-password',
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/RequestPassword')
  },
  {
    path: '/account/reset-password/confirm',
    name: 'reset-password',
    meta: { clearSession: true },
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/ResetPassword')
  },
  {
    path: '/account/recovery',
    name: 'account-recovery',
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/AccountRecovery')
  },
  {
    path: '/account/recovery/confirm',
    name: 'complete-recovery',
    meta: { clearSession: true },
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/CompleteRecovery')
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/FAQ')
  },
  {
    path: '/contact',
    name: 'contact-us',
    component: () => import(/* webpackChunkName: "loginView" */ '@/views/ContactUs')
  },
  {
    path: '/:ubcId(U-?\\d{4}-?\\d{4})',
    meta: { secure: true },
    component: () => import(/* webpackChunkName: "unionDetails" */ '@/views/details/UnionDetails'),
    children: [
      {
        path: 'training',
        name: 'training',
        meta: { secure: true },
        component: () => import(/* webpackChunkName: "training" */ '@/pages/training/views/Training')
      },
      {
        path: 'training/:page',
        name: 'training_link',
        props: true,
        meta: { secure: true },
        component: () => import(/* webpackChunkName: "training" */ '@/pages/training/views/Training')
      },
      {
        path: 'time_card',
        name: 'time_card',
        meta: { secure: true },
        component: () => import(/* webpackChunkName: "timeCard" */ '@/pages/time_card/TimeCard')
      }
    ]
  }
];

let appRouter = null;

const defaultPage = (router, store) => {
  return router.replace({
    name: 'training_link',
    params: {
      ubcId: store.getters['session/currentUser']?.ubcId,
      page: 'membership'
    }
  });
};

export default function getRouter(store) {
  if (!_.isNull(appRouter)) {
    return appRouter;
  }

  const router = (appRouter = new VueRouter({
    mode: 'history',
    base: '/app',
    routes
  }));

  store.watch(
    state => {
      return (state.session || {}).loggedIn;
    },
    loggedIn => {
      if (loggedIn) {
        Vue.nextTick(() => {
          const currentRoute = router.currentRoute;
          const returnUrl = getSafeReturnUrl(currentRoute);

          if (!!returnUrl) {
            store.commit('setDesiredRoute', returnUrl);
          }

          const desiredRoute = store.getters.desiredRoute;

          if (desiredRoute && currentRoute.name !== desiredRoute.name) {

            if (returnUrl === desiredRoute) {
              return window.location.href = desiredRoute;
            }

            store.dispatch('redirectSecureRoute', null);
            return router.replace(desiredRoute);
          }

          if ('login' == currentRoute.name) {
            return defaultPage(router, store);
          }
        });
      }
    }
  );

  router.afterEach(to => {
    if (typeof window.gtag !== 'function' || store.getters.meta?.env !== 'prod') {
      return;
    }

    window.gtag('event', 'page_view', {
      page_path: to.fullPath,
      page_title: to.name || document.title
    });
  });

  router.beforeEach(async (to, from, next) => {
    if (to.meta.clearSession && store.getters['session/isLoggedIn']) {
      await store.dispatch('session/logout');
    }

    const loggedIn = store.getters['session/isLoggedIn'];

    if (to.meta.secure && !loggedIn) {
      store.dispatch('redirectSecureRoute', to);
      return next({ name: 'login' });
    }

    if ('training' === to.name) {
      return defaultPage(router, store);
    }

    next();
  });

  return router;
}
